import { Outlet, useLocation, useNavigate, useMatch, useSearchParams } from 'react-router-dom'
import { Formik } from 'formik'
import { Button, TextField, IconButton, Alert, CircularProgress } from '@mui/material'
import { login } from 'utils/api/auth'
import { Link } from 'react-router-dom'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { lazy, Suspense, useEffect, useState } from 'react'
import {
  REQUEST_APPOINTMENT_KEY,
  APP_VERSION,
  REDIRECT_KEY,
  SECURE_STORAGE_KEY,
  TIMEOUT_KEY,
  TOKEN_KEY,
  USE_BIOMETRICS_KEY,
  ADDRESS_KEY,
  HAS_PREVIOUSLY_LOGGEDIN,
  WEBSITE_SELECTED_PRODUCTS,
} from 'app-constants'
import yup from 'utils/yup'
import { ReactComponent as Logo } from 'assets/icons/logo.svg'
import { Storage } from '@capacitor/storage'
import { Capacitor } from '@capacitor/core'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'

const FingerPrintButton = lazy(() => import('components/FingerPrintButton'))

const loginSchema = yup.object().shape({
  password: yup.string().required('Enter your password'),
  email: yup.string().email('enter a valid email').required('please enter email'),
})

const platFormMap = {
  eazymed: 'Eazymed',
  'fastklinik-hms': 'Fastklinik',
  'kwickpharm-pms': 'KwickPharm',
  findklinik: 'Findklinik',
}

function simplifyNames(input: string) {
  return [
    ...new Set(
      input
        .split(',')
        ?.map((name) => name.trim())
        ?.map((name) => platFormMap[name as keyof typeof platFormMap] || name)
    ),
  ]?.join(' or ')
}

function LoginPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isPasswordVisible, setPasswordVisibility] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [storedCredentials, setStoredCredentials] = useState<{ email: string; password: string }>()
  const [isLogginIn, setIsLogginIn] = useState(false)
  const isPage = useMatch('/login')
  const location = useLocation()
  const productsParam = JSON.parse(sessionStorage.getItem(WEBSITE_SELECTED_PRODUCTS) as string)
  const appointmentRequestUrl = sessionStorage?.[REQUEST_APPOINTMENT_KEY]
    ? JSON.parse(sessionStorage?.[REQUEST_APPOINTMENT_KEY])
    : ''
  const redirectUrl =
    (location.state as any)?.redirect ||
    sessionStorage.getItem(REDIRECT_KEY) ||
    appointmentRequestUrl?.pathname ||
    '/'

  function sendBroadCast(email: string) {
    const channel = new BroadcastChannel('loginChannel')
    channel.postMessage({
      currentLoggedInUser: JSON.stringify({ email }),
    })
    channel.close()
  }

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SecureStorage.get(SECURE_STORAGE_KEY)
        .then((result: any) => {
          setStoredCredentials(result)
        })
        .catch()
    }
  }, [])

  console.log(searchParams.get("products"))
  async function handleLogin(values: any) {
    try {
      setIsLogginIn(true)
      setLoginError('')
      const {
        token,
        token_ttl,
        user: { email, metadata, id },
      } = await login(values)
      sessionStorage.setItem(TOKEN_KEY, token)
      sessionStorage.setItem(TIMEOUT_KEY, token_ttl.toString())
      localStorage.setItem(HAS_PREVIOUSLY_LOGGEDIN, 'true')

      await Storage.set({
        key: 'token',
        value: token,
      })
      await Storage.set({
        key: 'id',
        value: id,
      })
      if (Capacitor.isNativePlatform()) {
        const storedAuth = await SecureStorage.get(SECURE_STORAGE_KEY)
        if (!storedAuth) {
          SecureStorage.set(SECURE_STORAGE_KEY, values)
        }
      }
      if (metadata.should_set_password) {
        navigate('set-new-password', { state: { token } })
        return
      } else {
        sendBroadCast(email)
        if (searchParams.has('redirect')) {
          navigate(`/providers/clinics`, { replace: true })
        } else if (searchParams.get('products')) {
          navigate('/easy-med?stage=add-quantity', { replace: true })
        } else {
          navigate(redirectUrl, { replace: true })
        }
      }

      if (localStorage[ADDRESS_KEY] && JSON.parse(localStorage[ADDRESS_KEY])?.email !== email) {
        localStorage.removeItem(ADDRESS_KEY)
      }
    } catch (error: any) {
      setLoginError(error.message)
    } finally {
      setIsLogginIn(false)
      sessionStorage.removeItem(REQUEST_APPOINTMENT_KEY)
    }
  }

  return (
    <div className="h-fit">
      <Outlet />
      {isPage && (
        <div className="lg:bg-white lg:shadow lg:p-10 p-5 lg:w-[35rem] lg:rounded-3xl">
          <div className="self-center grid place-items-center mb-1">
            <a href={import.meta.env.VITE_EAZYMED_WEBSITE_URL} target="_blank" rel="noreferrer">
              <Logo className="text-8xl w-[13rem] h-[8.93rem]" />
            </a>
          </div>
          <div className="mb-12 grid place-items-center">
            <h1 className="lg:text-center text-left title-mobile lg:title-desktop">
              Welcome Back 😃
            </h1>

            {searchParams.get('isOnPlatForm') === 'false' ? (
              !searchParams.get('platforms') ? (
                <p className="text-sm text-center text-light mt-2">
                  Your initial account setup wasn’t completed. Enter your signup password to
                  continue, or use <span className="font-semibold">Forgot Password</span> to reset
                  it.
                </p>
              ) : (
                <p className=" lg:text-center text-left subtitle-mobile lg:subtitle-desktop text-neutral-600  mt-2">
                  if you have an account on{' '}
                  <span className="font-semibold">
                    {simplifyNames(searchParams.get('platforms') || '')}
                  </span>
                  , please enter your password used there to continue
                </p>
              )
            ) : (
              <p className=" lg:text-center text-left subtitle-mobile lg:subtitle-desktop text-neutral-600  mt-2">
                Log In to your account
              </p>
            )}
          </div>
          <Formik
            initialValues={{
              email: (appointmentRequestUrl?.email as string) || searchParams.get('email') || '',
              password: '',
            }}
            validationSchema={loginSchema}
            onSubmit={handleLogin}
          >
            {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setValues }) => (
              <form onSubmit={handleSubmit}>
                <p className="mb-2 text-sm font-semibold">Your email address</p>
                <TextField
                  id="email"
                  type="email"
                  className="mb-8"
                  disabled={!!searchParams.get('email')}
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && !!errors.email}
                  helperText={touched.email ? errors.email : ''}
                  inputProps={{
                    className: 'rounded-[11px]',
                  }}
                  fullWidth
                />
                <p className="mb-2 text-sm font-semibold">Password</p>
                <TextField
                  id="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  className="border-red"
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && !!errors.password}
                  placeholder="Type your password"
                  helperText={touched.password ? errors.password : ''}
                  fullWidth
                  inputProps={{
                    className: 'rounded-[11px]',
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setPasswordVisibility(!isPasswordVisible)}
                        className="text-neutral-600"
                        aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                      >
                        {!isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                        <span className="sr-only">
                          {isPasswordVisible ? 'Hide Password' : 'Show Password'}
                        </span>
                      </IconButton>
                    ),
                  }}
                />
                <div className="flex justify-between items-center mt-3">
                  <Link to="/forgot-password" className="text-brand-700 font-medium text-xs">
                    Forgot Password
                  </Link>
                  <Link to="/signup" className="text-brand-700 font-medium text-xs">
                    Create an account
                  </Link>
                </div>
                <div className="flex items-center gap-4 mt-8">
                  <Button
                    type="submit"
                    disabled={isSubmitting || isLogginIn}
                    className="p-3 rounded-md"
                    fullWidth
                  >
                    {isSubmitting || isLogginIn ? (
                      <>
                        <CircularProgress color="inherit" className="text-white" size={22} />
                        <span className="sr-only">Submitting</span>
                      </>
                    ) : (
                      'LOG IN'
                    )}
                  </Button>
                  {Capacitor.isNativePlatform() &&
                    storedCredentials &&
                    localStorage?.[USE_BIOMETRICS_KEY] && (
                      <Suspense>
                        <FingerPrintButton
                          next={() => {
                            setValues(
                              {
                                email: storedCredentials?.email,
                                password: storedCredentials?.password,
                              },
                              false
                            )

                            handleLogin({
                              email: storedCredentials?.email,
                              password: storedCredentials?.password,
                            })
                          }}
                        />
                      </Suspense>
                    )}
                </div>
                <Alert
                  severity="error"
                  className={`mt-4 ${!loginError ? 'opacity-0' : ''}`}
                  aria-hidden={!loginError}
                  data-test-id="login-server-error"
                >
                  {loginError}
                </Alert>

                {Capacitor.isNativePlatform() && (
                  <div className="text-center mt-10">
                    <span className="text-xs">v</span>
                    {`${APP_VERSION}`}
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      )}
    </div>
  )
}

export default LoginPassword
